import { useMemo } from 'react';

import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useAppInitialization } from 'hooks/useAppInitialization';

interface InitializationStep {
  id: string;
  name: string;
  description: string;
  completed: boolean;
  progress: number;
}

/**
 * Hook centralizado para rastrear o progresso real da inicialização
 * Evita múltiplos AppLoading e proporciona feedback granular
 */
export const useInitializationProgress = () => {
  const { token, user, isLoading: userLoading } = useUser();
  const { menu, isLoading: menuLoading } = useMenu();
  const { isInitialized: menuSelectionInitialized } = useMenuSelection();
  const { hasInitialized } = useAppInitialization();

  const steps = useMemo<InitializationStep[]>(() => {
    const allSteps: InitializationStep[] = [
      {
        id: 'login',
        name: 'Processo de Login',
        description: 'Autenticando usuário...',
        completed: !!token && !!user,
        progress: !token ? 0 : !user ? 50 : 100,
      },
      {
        id: 'token',
        name: 'Token de Acesso',
        description: 'Verificando autenticação...',
        completed: !!token,
        progress: !!token ? 100 : 0,
      },
      {
        id: 'user',
        name: 'Dados do Usuário',
        description: 'Carregando informações do usuário...',
        completed: !!user && !userLoading,
        progress: userLoading ? 50 : !!user ? 100 : 0,
      },
      {
        id: 'menu',
        name: 'Menu de Navegação',
        description: 'Configurando menu de navegação...',
        completed: menu.length > 0 && !menuLoading,
        progress: menuLoading ? 50 : menu.length > 0 ? 100 : 0,
      },
      {
        id: 'menuSelection',
        name: 'Seleção de Menu',
        description: 'Inicializando sistema de seleção...',
        completed: menuSelectionInitialized,
        progress: menuSelectionInitialized ? 100 : 0,
      },
      {
        id: 'finalization',
        name: 'Finalização',
        description: 'Finalizando configuração...',
        completed: hasInitialized,
        progress: hasInitialized ? 100 : 0,
      },
    ];

    return allSteps;
  }, [token, user, userLoading, menu, menuLoading, menuSelectionInitialized, hasInitialized]);

  const overallProgress = useMemo(() => {
    const totalSteps = steps.length;
    const completedSteps = steps.filter(step => step.completed).length;
    const progressSum = steps.reduce((sum, step) => sum + step.progress, 0);

    return Math.round(progressSum / totalSteps);
  }, [steps]);

  const currentStep = useMemo(() => {
    return steps.find(step => !step.completed) || steps[steps.length - 1];
  }, [steps]);

  const isInitializing = useMemo(() => {
    const result = !hasInitialized || userLoading || menuLoading || !menuSelectionInitialized;
    console.log('[useInitializationProgress] Estado de inicialização:', {
      hasInitialized,
      userLoading,
      menuLoading,
      menuSelectionInitialized,
      isInitializing: result,
      token: !!token,
      user: !!user,
      menuLength: menu.length,
    });
    return result;
  }, [
    hasInitialized,
    userLoading,
    menuLoading,
    menuSelectionInitialized,
    token,
    user,
    menu.length,
  ]);

  const initializationStatus = useMemo(() => {
    if (hasInitialized) {
      return {
        status: 'completed' as const,
        message: 'Aplicação pronta!',
        description: 'Todas as configurações foram carregadas com sucesso.',
      };
    }

    if (userLoading) {
      return {
        status: 'loading' as const,
        message: 'Carregando dados do usuário...',
        description: 'Aguarde enquanto carregamos suas informações.',
      };
    }

    if (menuLoading) {
      return {
        status: 'loading' as const,
        message: 'Configurando menu...',
        description: 'Preparando sua área de trabalho.',
      };
    }

    if (!menuSelectionInitialized) {
      return {
        status: 'loading' as const,
        message: 'Inicializando navegação...',
        description: 'Configurando sistema de navegação.',
      };
    }

    return {
      status: 'loading' as const,
      message: 'Inicializando aplicação...',
      description: 'Preparando tudo para você.',
    };
  }, [hasInitialized, userLoading, menuLoading, menuSelectionInitialized]);

  return {
    steps,
    overallProgress,
    currentStep,
    isInitializing,
    initializationStatus,
    hasInitialized,
  };
};
